import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "./scss/Settings.scss";
import logo from "../images/logo.png";
import house from "../images/settings/Icon.png";
import user from "../images/settings/user.png";
import email from "../images/settings/email.png";
import phone_icon from "../images/settings/phone_icon.png";

const IconUser = () => <img src={user} alt="user" />;
const IconMail = () => <img src={email} alt="email" />;
const IconPhone = () => <img src={phone_icon} alt="phone" />;
const IconHome = () => <img src={house} alt="requisites" />;

const getInitials = (name) => {
  if (!name) return "U";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const formatStreet = (address, emptyValue) => {
  if (!address) return emptyValue;

  return [address.streetName, address.houseNumber].filter(Boolean).join(" ");
};

const formatCityPostCode = (address, emptyValue) => {
  if (!address) return emptyValue;

  return [address.city, address.postalCode].filter(Boolean).join(", ");
};

const getManagerName = (manager, emptyValue) => {
  if (!manager) return emptyValue;

  return [manager.firstName, manager.lastName].filter(Boolean).join(" ");
};

const InfoRow = ({ icon, label, value, emptyValue }) => (
  <div className="settings-info-row">
    <div className="settings-info-row__icon">{icon}</div>

    <div>
      <span>{label}</span>
      <strong>{value || emptyValue}</strong>
    </div>
  </div>
);

const DetailsCard = ({
  title,
  name,
  address,
  phone,
  email,
  workingHours,
  vatCode,
  showCompanyFields,
  emptyValue,
  t,
}) => (
  <div className="settings-details-card">
    <div className="settings-details-card__head">
      <div className="settings-details-card__title">
        <div className="settings-details-card__icon">
          <IconHome />
        </div>

        <h3>{title}</h3>
      </div>
    </div>

    <div className="settings-details-card__body">
      <p className="settings-details-card__name">{name || emptyValue}</p>
      <p>
        {t("settings.street")} {" "}
        <span className="settings-details-card__name">
          {formatStreet(address, emptyValue)}
        </span>
      </p>
      <p>
        {t("settings.cityPostCode")} {" "}
        <span className="settings-details-card__name">
          {formatCityPostCode(address, emptyValue)}
        </span>
      </p>
      <p>
        {t("settings.country")} {" "}
        <span className="settings-details-card__name">
          {address?.country || emptyValue}
        </span>
      </p>
      <p>
        {t("settings.phone")} {" "}
        <span className="settings-details-card__name">{phone || emptyValue}</span>
      </p>
      <p>
        {t("settings.email")} {" "}
        <span className="settings-details-card__name">{email || emptyValue}</span>
      </p>

      {showCompanyFields ? (
        <>
          <p>
            {t("settings.workingHours")} {" "}
            <span className="settings-details-card__name">
              {workingHours || emptyValue}
            </span>
          </p>
          <p>
            {t("settings.vatCode")} {" "}
            <span className="settings-details-card__name">
              {vatCode || emptyValue}
            </span>
          </p>
        </>
      ) : null}
    </div>
  </div>
);

const Settings = () => {
  const { t } = useTranslation();
  const emptyValue = t("common.empty");

  document.title = `Gudema - ${t("settings.title")}`;

  const [details, setDetails] = useState(null);

  useEffect(() => {
    axios
      .get("/api/user")
      .then((response) => {
        setDetails(response.data.details || {});
      })
      .catch((err) => {
        console.error(err);
        setDetails({});
      });
  }, []);

  if (details === null) {
    return (
      <main className="settings-page">
        <div className="settings-page__container">
          <h1>{t("settings.title")}</h1>
          <div className="settings-page__loading">{t("settings.loading")}</div>
        </div>
      </main>
    );
  }

  const clientName = details.name || details.username || emptyValue;
  const initials = getInitials(clientName);

  const manager = details.responsibleManager;

const managerName = getManagerName(manager, emptyValue);
const managerInitials = manager
  ? getInitials(managerName)
  : "EG";

const managerEmail = manager?.email || "info@gudema.lt";
const managerPhone = manager?.phone || "+370 673 62 767";

  return (
    <main className="settings-page">
      <div className="settings-page__container">
        <h1>{t("settings.title")}</h1>

        <div className="settings-layout">
          <section className="settings-profile">
            <div className="settings-profile__top">
              <div className="settings-profile__avatar">{initials}</div>

              <div className="settings-profile__identity">
                <h2>{clientName}</h2>
                <p>{details.email || emptyValue}</p>
              </div>
            </div>

            <div className="settings-profile__info">
              <div className="settings-profile__info-head">
                <h3>{t("settings.personalInfo")}</h3>
              </div>

              <div className="settings-profile__rows">
                <InfoRow
                  icon={<IconUser />}
                  label={t("settings.fullName")}
                  value={clientName}
                  emptyValue={emptyValue}
                />

                <InfoRow
                  icon={<IconMail />}
                  label={t("settings.email")}
                  value={details.email}
                  emptyValue={emptyValue}
                />

                <InfoRow
                  icon={<IconPhone />}
                  label={t("settings.phone")}
                  value={details.phone}
                  emptyValue={emptyValue}
                />
              </div>
            </div>
          </section>

          <section className="settings-side">
            <div className="settings-side__cards">
              <DetailsCard
                title={t("settings.companyRequisites")}
                name={details.name}
                address={details.primaryAddress}
                phone={details.phone}
                email={details.email}
                workingHours={details.workingHours}
                vatCode={details.vatCode}
                showCompanyFields={true}
                emptyValue={emptyValue}
                t={t}
              />

              <DetailsCard
                title={t("settings.deliveryAddress")}
                name={details.name}
                address={details.secondaryAddress}
                phone={details.phone}
                email={details.email}
                showCompanyFields={false}
                emptyValue={emptyValue}
                t={t}
              />
            </div>

            
            <div className="settings-manager">
              <div className="settings-manager__head">
                <div className="settings-manager__avatar">
                  {managerInitials}
                </div>

                <h3>{t("settings.manager")}</h3>
              </div>

              <div className="settings-manager__contacts">
                <strong>{manager ? managerName : "Edgaras Gudauskas"}</strong>
                <span>{managerEmail}</span>
                <span>{managerPhone}</span>
              </div>

              <img src={logo} alt="Gudema" />
            </div>

          </section>
        </div>
      </div>
    </main>
  );
};

export default Settings;
